import { useState, useEffect, useMemo, useCallback } from "react";
import "./style/visitorTypes.scss";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store/store";
import { clearAuth } from "../../store/slices/authSlice";
import { Button } from "react-bootstrap";
import { deleteAllCookies } from "../../utils/helpers";
import { touchlessService } from "../../services/api/touchlessService";
import ConfirmationModal from "../../components/common/ConfirmationModal/ConfirmationModal";
import type { VisitorType } from "../../types/orgConfig";
import { BackButton } from "../../components/ui/Button/backButton";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { useStrings } from "../../hooks/useStrings";

const VisitorTypes: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { updateVisitorTypeId, saveVideoUrl } = useVisitorData();

  // Modal state for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Lazy initialization - read from sessionStorage synchronously to avoid cascading renders
  const { config, themeColors } = useAppConfig();

  const [selectedVisitorType, setSelectedVisitorType] = useState<string | null>(
    null,
  );

  const purposeMessage = config?.purposeMessage ?? "";

  // Handle cancel - clears session and navigates to home
  const handleCancel = () => {
    dispatch(clearAuth());
    sessionStorage.clear();
    deleteAllCookies();
    setShowConfirmationModal(false);
    navigate("/");
  };

  // Memoize to prevent useEffect from running on every render
  const allVisitorTypes = useMemo<VisitorType[]>(
    () => config?.visitorType?.filter((val) => val.showOnIpad === true) || [],
    [config?.visitorType],
  );

  const handleVisitorType = useCallback(
    (id: string, url?: string | null | undefined, typeName?: string) => {
      updateVisitorTypeId(id);

      if (!touchlessService.isNullUndefinedOrBlank(url) && url) {
        saveVideoUrl(url);
      }

      if (typeName) {
        import("../../services/middleware/middlewareService")
          .then((m) => m.middlewareService.setUser({ visitorType: typeName }))
          .catch(() => {
            /* RUM is best-effort; never block the flow on logging */
          });
      }
    },
    [updateVisitorTypeId, saveVideoUrl],
  );

  const handleContinueClick = useCallback(() => {
    navigate("/select-host");
  }, [navigate]);

  // Auto-navigate if only one visitor type
  useEffect(() => {
    if (allVisitorTypes.length === 1) {
      handleVisitorType(
        allVisitorTypes[0].id,
        allVisitorTypes[0].videoUrl,
        allVisitorTypes[0].visitorType,
      );
      handleContinueClick();
    }
  }, [allVisitorTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const onBack = () => {
    navigate("/full-name");
  };

  return (
    <div className="main-screen" data-testid="visitor-type-main-screen">
      <div
        className="screen-outer"
        id="visitor-type"
        data-testid="visitor-type-screen-outer"
      >
        {/* Header */}
        <div
          className="screen-header-fixed"
          data-testid="visitor-type-header-fixed"
        >
          <div
            className="header-fixed-left"
            data-testid="visitor-type-header-left"
          >
            <BackButton onBack={onBack} />
          </div>
          <div
            className="header-fixed-right"
            data-testid="visitor-type-header-right"
          ></div>
        </div>

        {/* Main Content */}
        <div className="screen-inner" data-testid="visitor-type-screen-inner">
          <div
            className="screen-subinner"
            data-testid="visitor-type-screen-subinner"
          >
            <div className="screen-body" data-testid="visitor-type-screen-body">
              <h5 className="screen-title" data-testid="visitor-type-title">
                {purposeMessage || S.VISITOR_TYPES.FALLBACK_TITLE}
              </h5>
              <p
                className="screen-subtitle"
                data-testid="visitor-type-subtitle"
              >
                {S.VISITOR_TYPES.SUBTITLE}
              </p>

              <ul className="visitor-type-list" data-testid="visitor-type-list">
                {allVisitorTypes.map((vtype) => (
                  <li
                    key={vtype.id}
                    data-testid={`visitor-type-list-item-${vtype.id}`}
                  >
                    {vtype.status === "ACTIVE" && (
                      <Button
                        variant="outline-primary"
                        className={`btn btn-type ${selectedVisitorType === vtype.id ? "active" : ""}`}
                        onClick={() => {
                          handleVisitorType(vtype.id, vtype.videoUrl, vtype.visitorType);
                          setSelectedVisitorType(vtype.id);
                        }}
                        data-testid={`visitor-type-button-${vtype.id}`}
                      >
                        {vtype.visitorType}
                        <i
                          className="bi bi-check-circle"
                          style={{ color: themeColors.btnTextColor }}
                          data-testid="visitor-type-arrow-icon"
                        ></i>
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="screen-footer"
              data-testid="visitor-type-screen-footer"
            >
              <button
                type="button"
                className="btn btn-primary btn-width-full font-weight-semibold"
                data-testid="visitor-type-continue-button"
                disabled={selectedVisitorType === null}
                onClick={() => handleContinueClick()}
              >
                {S.COMMON.CONTINUE} &nbsp;&nbsp;
                <i className="bi bi-arrow-right-short"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleCancel}
      />
    </div>
  );
};

export default VisitorTypes;
